/**
 * Desktop Performance Optimizations
 * 
 * This module provides utilities and components specifically optimized
 * for desktop PageSpeed Insights scores, focusing on:
 * - Reducing Total Blocking Time (TBT)
 * - Improving Largest Contentful Paint (LCP)
 * - Preventing Cumulative Layout Shift (CLS)
 * - Implementing efficient cache headers awareness
 */

import { ReactNode, useEffect, useState, memo } from "react";

/**
 * Detects if the current device is desktop
 * Uses both screen size and pointer capability
 */
export function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const checkDesktop = () => {
      // Desktop: large screen AND fine pointer (mouse)
      const isLargeScreen = window.innerWidth >= 1024;
      const hasFinePointer = window.matchMedia("(pointer: fine)").matches;
      setIsDesktop(isLargeScreen && hasFinePointer);
    };

    checkDesktop();
    window.addEventListener("resize", checkDesktop);
    return () => window.removeEventListener("resize", checkDesktop);
  }, []);

  return isDesktop;
}

/**
 * DeferAnimations - Defers heavy animations until after LCP
 * 
 * On desktop, animations can block the main thread and hurt TBT.
 * This component delays rendering children until the browser is idle.
 */
interface DeferAnimationsProps {
  children: ReactNode;
  /** Delay in ms before showing animated content (default: 100) */
  delay?: number;
  /** Fallback content to show while loading */
  fallback?: ReactNode;
}

export function DeferAnimations({
  children,
  delay = 100,
  fallback = null,
}: DeferAnimationsProps) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Use requestIdleCallback for optimal timing
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(
        () => setReady(true),
        { timeout: delay + 50 }
      );
      return () => window.cancelIdleCallback(id);
    } else {
      const timer = setTimeout(() => setReady(true), delay);
      return () => clearTimeout(timer);
    }
  }, [delay]);

  return <>{ready ? children : fallback}</>;
}

/**
 * DesktopIframeWrapper - Prevents CLS from iframe loading
 * 
 * Wraps iframes with a skeleton placeholder and reserves layout space
 * to prevent Cumulative Layout Shift (CLS) issues on desktop.
 */
interface DesktopIframeWrapperProps {
  src: string;
  title: string;
  height: number;
  width?: string;
  className?: string;
}

function DesktopIframeWrapperComponent({
  src,
  title,
  height,
  width = "100%",
  className = "",
}: DesktopIframeWrapperProps) {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div
      className={`relative ${className}`}
      style={{
        height: `${height}px`,
        width,
        contain: "strict",
      }}
    >
      {/* Skeleton placeholder */}
      {!isLoaded && (
        <div 
          className="absolute inset-0 bg-muted/30 animate-pulse rounded-xl"
          aria-hidden="true"
        />
      )}
      
      <iframe
        src={src}
        title={title}
        aria-label={title}
        width="100%"
        height={height}
        className={`transition-opacity duration-300 ${isLoaded ? "opacity-100" : "opacity-0"}`}
        style={{
          border: "none",
          display: "block",
        }}
        loading="lazy"
        onLoad={() => setIsLoaded(true)}
      />
    </div>
  );
}

export const DesktopIframeWrapper = memo(DesktopIframeWrapperComponent);

/**
 * PreloadDesktopAssets - Component that preloads critical desktop assets
 * 
 * Adds link preload hints for desktop-critical resources to improve LCP.
 * Should be placed in the document head or at the top of the page.
 */
export function PreloadDesktopAssets() {
  useEffect(() => {
    // Only run on desktop
    if (window.innerWidth < 1024) return;

    // Preload critical fonts for desktop
    const fontLinks = [
      "https://fonts.gstatic.com/s/montserrat/v31/JTUSjIg1_i6t8kCHKm459WlhyyTh89Y.woff2",
    ];

    fontLinks.forEach((href) => {
      const existing = document.querySelector(`link[href="${href}"]`);
      if (!existing) {
        const link = document.createElement("link");
        link.rel = "preload";
        link.as = "font";
        link.type = "font/woff2";
        link.href = href;
        link.crossOrigin = "anonymous";
        document.head.appendChild(link);
      }
    });
  }, []);

  return null;
}

/**
 * ReduceMotionOptimizer - Respects prefers-reduced-motion
 * 
 * Disables heavy animations when user has requested reduced motion,
 * which also improves performance metrics.
 */
export function useReducedMotion(): boolean {
  const [prefersReduced, setPrefersReduced] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReduced(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => setPrefersReduced(e.matches);
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  return prefersReduced;
}

/**
 * CacheAwareImage - Image component that hints browser caching
 * 
 * Uses proper attributes to enable efficient browser caching
 * and reduce network requests on repeat visits.
 */
interface CacheAwareImageProps {
  src: string;
  alt: string;
  width: number;
  height: number;
  className?: string;
  loading?: "lazy" | "eager";
  priority?: boolean;
}

export function CacheAwareImage({
  src,
  alt,
  width,
  height,
  className = "",
  loading = "lazy",
  priority = false,
}: CacheAwareImageProps) {
  return (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      className={className}
      loading={priority ? "eager" : loading}
      decoding={priority ? "sync" : "async"}
      fetchPriority={priority ? "high" : undefined}
    />
  );
}

export default {
  useIsDesktop,
  DeferAnimations,
  DesktopIframeWrapper,
  PreloadDesktopAssets,
  useReducedMotion,
  CacheAwareImage,
};
